import { isDefined } from './guards';

/**
 * Time unit multipliers in milliseconds
 */
const TIME_UNIT_MULTIPLIERS = {
	ms: 1,
	millisecond: 1,
	second: 1000,
	minute: 60 * 1000,
	hour: 60 * 60 * 1000,
	day: 24 * 60 * 60 * 1000,
} as const;
type TimeUnit = keyof typeof TIME_UNIT_MULTIPLIERS;

/**
 * Template literal type for time strings like "5 seconds", "10 hours"
 * Supports both singular and plural forms of time units
 */
export type TimeString = `${number} ${TimeUnit | `${TimeUnit}s`}`;

/**
 * Creates a time duration in milliseconds from a human-readable string
 *
 * @param timeString - A string in the format "value unit" (e.g., "5 seconds", "10 hours")
 * @returns The total duration in milliseconds
 * @throws Error if the time string format is invalid or the unit is not recognized
 *
 * @example
 * ```typescript
 * // 5 minutes in milliseconds
 * const fiveMinutes = makeTime("5 minutes"); // 300000
 *
 * // 1 day in milliseconds
 * const oneDay = makeTime("1 day"); // 86400000
 *
 * // 30 seconds in milliseconds
 * const thirtySeconds = makeTime("30 seconds"); // 30000
 *
 * // Fractional values
 * const oneAndHalfHours = makeTime("1.5 hours"); // 5400000
 *
 * // Negative values
 * const minusFiveMinutes = makeTime("-5 minutes"); // -300000
 * ```
 */
export function makeTime(timeString: TimeString): number {
	/** parse the time string to extract value and unit */
	const match = timeString.match(/^(-?\d*\.?\d+)\s+(\w+)$/);
	const invalidError = new Error(`Invalid time string format: ${timeString}. Expected format: "value unit" (e.g., "5 seconds")`);

	/** validate the inputs */
	if (!isDefined(match)) throw invalidError;
	const [, valueStr, unit] = match;
	if (!isDefined(valueStr)) throw invalidError;

	/** get the base unit by removing trailing 's' if present */
	const baseUnit = unit !== 'ms' && unit?.endsWith('s') ? unit.slice(0, -1) : unit;

	/** check if the unit is valid */
	if (!isDefined(TIME_UNIT_MULTIPLIERS[baseUnit as TimeUnit])) {
		throw new Error(`Invalid time unit: ${unit}. Valid units are: ${Object.keys(TIME_UNIT_MULTIPLIERS).join(', ')} (with optional 's' suffix)`);
	}

	/** parse the value */
	const value = parseFloat(valueStr);

	/** return the value in milliseconds */
	return value * TIME_UNIT_MULTIPLIERS[baseUnit as TimeUnit];
}
